/**
 * Системы техкарты — слой над `/api/v1/planning/tech-card-modules/*` (AK-773).
 *
 * Решение founder Р35: система защиты / питания / обработки почвы — это
 * САМОСТОЯТЕЛЬНАЯ мини-техкарта. Она живёт отдельно от техкарт, вставляется в
 * любую из них и заменяется ЦЕЛИКОМ с пересчётом стоимости карты. До этого
 * набор операций защиты существовал только внутри конкретного варианта карты,
 * и «взять защиту из интенсива» означало клонировать карту целиком.
 *
 * Макет редактора — `docs/superpowers/prototypes/season-planning-2027/07-system-editor.html`
 * (утверждён founder). Оттуда же язык экрана: «Системы», «Вставить систему»,
 * «Заменить систему», «Против чего», «Обработки». Модельные слова (kind,
 * module_id, dry_run) наружу не отдаём.
 *
 * Строка системы устроена ТАК ЖЕ, как строка техкарты (`OperationIn` того же
 * API), поэтому редактор системы переиспользует диалоги операции и материалов
 * с экрана техкарт — второй формы для «того же самого» быть не должно.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { pluralRu } from '@/lib/dateFmt';
import { techCardKeys, techCardModuleKeys } from '@/lib/planning/queryKeys';
import { toast } from '@/lib/toast';
import {
  techCardErrorText,
  type CardCost,
  type MaterialInput,
  type OperationInput,
  type OperationPatch,
  type TechCardMaterial,
  type TechCardOperation,
} from '@/lib/planning/techCards';

export { techCardModuleKeys };

// ── Типы систем ──────────────────────────────────────────────────────────────

/** Три системы Р35. Совпадает с блоками техкарты — это не совпадение: система и есть блок, вынесенный наружу. */
export type ModuleKind = 'protection' | 'nutrition' | 'tillage';

export const MODULE_KIND_ORDER: readonly ModuleKind[] = ['protection', 'nutrition', 'tillage'];

/** Полное название — заголовок редактора и подпись строки в карте. */
export const MODULE_KIND_TITLES: Record<ModuleKind, string> = {
  protection: 'Система защиты',
  nutrition: 'Система питания',
  tillage: 'Система обработки почвы',
};

/** Короткое — фильтр библиотеки и колонка «тип» (в таблице длинное не помещается). */
export const MODULE_KIND_SHORT: Record<ModuleKind, string> = {
  protection: 'Защита',
  nutrition: 'Питание',
  tillage: 'Обработка почвы',
};

export function moduleKindTitle(kind: string | null | undefined): string {
  if (!kind) return 'Система';
  return MODULE_KIND_TITLES[kind as ModuleKind] ?? 'Система';
}

/**
 * Колонка «Против чего» есть ТОЛЬКО у защиты (правые боксы макета 07).
 *
 * У питания вместо неё приём и вынос, у обработки почвы — глубина: пустая
 * колонка «против чего» на системе питания читается как «забыли заполнить».
 */
export function showsTargetColumn(kind: ModuleKind): boolean {
  return kind === 'protection';
}

/** Статусы — те же три слова, что у техкарты: черновик / готова / убрана. */
export type ModuleStatus = 'draft' | 'ready' | 'archived';

export const MODULE_STATUS_TITLES: Record<ModuleStatus, string> = {
  draft: 'Черновик',
  ready: 'Утверждена',
  archived: 'Убрана',
};

// ── Ответы API ───────────────────────────────────────────────────────────────

export interface SystemModule {
  id: string;
  kind: ModuleKind;
  /** Название типа с бэка; локальное `MODULE_KIND_TITLES` — запасной вариант. */
  kind_title?: string | null;
  name: string;
  crop_id: string | null;
  status: ModuleStatus;
  operations_count: number;
  /** «Где используется» — в скольких техкартах эта система стоит. */
  used_in_cards_count: number;
  cost: { per_ha: number | null } | null;
  season_id?: string | null;
  farm_id?: string | null;
  target_yield_min_c_ha?: number | null;
  target_yield_max_c_ha?: number | null;
  notes?: string | null;
}

/**
 * Строка системы = строка техкарты плюс две подписи, которые есть только у
 * мини-техкарты: фенофаза (окно у системы задаётся по фазе культуры, даты
 * подставит сезон карты) и вредный объект («против чего»).
 *
 * Обе необязательные: пока бэк их не отдаёт, колонки честно показывают
 * прочерк, а не выдуманное значение.
 */
export interface ModuleOperation extends TechCardOperation {
  phase_label?: string | null;
  target_label?: string | null;
  machinery_unit_name?: string | null;
}

export interface ModuleUsage {
  tech_card_id: string;
  tech_card_name: string;
  farm_name: string | null;
}

/**
 * Карточка системы. Ответ ПЛОСКИЙ (поля системы + состав), как у техкарты:
 * `GET /tech-card-modules/{id}` отдаёт саму систему с обработками, материалами
 * по строкам, стоимостью и списком карт, где она стоит.
 */
export interface ModuleDetail extends SystemModule {
  operations: ModuleOperation[];
  /** Материалы по строкам: ключ — id строки. */
  materials: Record<string, TechCardMaterial[]>;
  cost: CardCost | null;
  used_in: ModuleUsage[];
}

/** Система, вставленная в техкарту — строка блока «Системы» карточки карты. */
export interface CardModuleRow {
  module_id: string;
  name: string;
  kind: ModuleKind;
  kind_title?: string | null;
  operations_count: number;
  /** ₽/га системы внутри карты. Может не приехать — тогда денег не рисуем. */
  cost_per_ha?: number | null;
}

/**
 * Ответ вставки/замены — та же форма, что у замены блока техкарты
 * (`/tech-cards/{id}/blocks/{kind}/replace`), поэтому предпросмотр
 * «было → станет → дельта» рисуется одним и тем же кодом.
 */
export interface ModuleApplyResult {
  before: CardCost;
  after: CardCost;
  delta_per_ha: number;
  /** `false` — это предпросмотр: в БД ничего не изменилось. */
  applied: boolean;
}

// ── Чтение ───────────────────────────────────────────────────────────────────

export interface ModuleListFilter {
  seasonId: string | null;
  farmId?: string | null;
  kind?: ModuleKind | null;
  cropId?: string | null;
}

export function useSystemModules({ seasonId, farmId = null, kind = null, cropId = null }: ModuleListFilter) {
  return useQuery<SystemModule[], Error>({
    queryKey: techCardModuleKeys.list(seasonId, farmId, kind, cropId),
    queryFn: async () => {
      const qs = new URLSearchParams({ season_id: seasonId as string });
      if (farmId) qs.set('farm_id', farmId);
      if (kind) qs.set('kind', kind);
      if (cropId) qs.set('crop_id', cropId);
      const resp = await api.get<{ items: SystemModule[] }>(
        `/api/v1/planning/tech-card-modules?${qs.toString()}`,
      );
      return resp?.items ?? [];
    },
    enabled: !!seasonId,
    staleTime: 30_000,
  });
}

export function useSystemModule(moduleId: string | null) {
  return useQuery<ModuleDetail, Error>({
    queryKey: techCardModuleKeys.module(moduleId),
    queryFn: () => api.get<ModuleDetail>(`/api/v1/planning/tech-card-modules/${moduleId}`),
    enabled: !!moduleId,
    staleTime: 30_000,
  });
}

/** Системы, вставленные в техкарту — блок «Системы» карточки карты. */
export function useCardModules(cardId: string | null) {
  return useQuery<CardModuleRow[], Error>({
    queryKey: techCardModuleKeys.ofCard(cardId),
    queryFn: async () => {
      const resp = await api.get<{ items: CardModuleRow[] }>(
        `/api/v1/planning/tech-cards/${cardId}/modules`,
      );
      return resp?.items ?? [];
    },
    enabled: !!cardId,
    staleTime: 30_000,
  });
}

/**
 * Предпросмотр вставки — запрос, а не мутация: агроном щёлкает системами
 * туда-сюда, сравнивая цифры, и повторный выбор той же системы не должен
 * бить в сеть. Ответ `dry_run` ничего не пишет.
 */
export function useInsertModulePreview(cardId: string | null, moduleId: string | null) {
  return useQuery<ModuleApplyResult, Error>({
    queryKey: techCardModuleKeys.insertPreview(cardId ?? '', moduleId ?? ''),
    queryFn: () =>
      api.post<ModuleApplyResult>(`/api/v1/planning/tech-cards/${cardId}/modules/insert`, {
        module_id: moduleId,
        dry_run: true,
      }),
    enabled: !!cardId && !!moduleId,
    retry: false,
    staleTime: 30_000,
  });
}

export function useReplaceModulePreview(
  cardId: string | null,
  oldModuleId: string | null,
  newModuleId: string | null,
) {
  return useQuery<ModuleApplyResult, Error>({
    queryKey: techCardModuleKeys.replacePreview(cardId ?? '', oldModuleId ?? '', newModuleId ?? ''),
    queryFn: () =>
      api.post<ModuleApplyResult>(`/api/v1/planning/tech-cards/${cardId}/modules/replace`, {
        old_module_id: oldModuleId,
        new_module_id: newModuleId,
        dry_run: true,
      }),
    enabled: !!cardId && !!oldModuleId && !!newModuleId,
    retry: false,
    staleTime: 30_000,
  });
}

// ── Запись ───────────────────────────────────────────────────────────────────

/**
 * Что протухает после правки системы.
 *
 * Гасим ОБА неймспейса: система живёт своей жизнью, но её строки стоят внутри
 * техкарт — правка нормы в системе меняет ₽/га каждой карты, куда она
 * вставлена. Инвалидация только систем оставила бы соседний экран с прежними
 * деньгами до F5.
 */
function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: techCardModuleKeys.all() });
    void qc.invalidateQueries({ queryKey: techCardKeys.all() });
  };
}

function notifyFailure(err: unknown): void {
  toast.error(techCardErrorText(err));
}

export interface CreateModuleInput {
  seasonId: string;
  farmId?: string | null;
  kind: ModuleKind;
  name: string;
  cropId?: string | null;
  targetYieldMinCHa?: number | null;
  targetYieldMaxCHa?: number | null;
  notes?: string | null;
}

export function useCreateSystemModule() {
  const invalidate = useInvalidate();
  return useMutation<SystemModule, Error, CreateModuleInput>({
    mutationFn: (input) =>
      api.post<SystemModule>('/api/v1/planning/tech-card-modules', {
        season_id: input.seasonId,
        farm_id: input.farmId ?? null,
        kind: input.kind,
        name: input.name.trim(),
        crop_id: input.cropId ?? null,
        target_yield_min_c_ha: input.targetYieldMinCHa ?? null,
        target_yield_max_c_ha: input.targetYieldMaxCHa ?? null,
        notes: input.notes ?? null,
      }),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

export interface PatchModuleInput {
  moduleId: string;
  patch: {
    name?: string;
    crop_id?: string | null;
    status?: ModuleStatus;
    target_yield_min_c_ha?: number | null;
    target_yield_max_c_ha?: number | null;
    notes?: string | null;
  };
}

export function usePatchSystemModule() {
  const invalidate = useInvalidate();
  return useMutation<SystemModule, Error, PatchModuleInput>({
    mutationFn: ({ moduleId, patch }) =>
      api.patch<SystemModule>(`/api/v1/planning/tech-card-modules/${moduleId}`, patch),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

export function useArchiveSystemModule() {
  const invalidate = useInvalidate();
  return useMutation<unknown, Error, { moduleId: string }>({
    mutationFn: ({ moduleId }) =>
      api.post(`/api/v1/planning/tech-card-modules/${moduleId}/archive`),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

export function useAddModuleOperation(moduleId: string | null) {
  const invalidate = useInvalidate();
  return useMutation<ModuleOperation, Error, OperationInput>({
    mutationFn: (body) =>
      api.post<ModuleOperation>(`/api/v1/planning/tech-card-modules/${moduleId}/operations`, body),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

export function usePatchModuleOperation(moduleId: string | null) {
  const invalidate = useInvalidate();
  return useMutation<ModuleOperation, Error, { opId: string; patch: OperationPatch }>({
    mutationFn: ({ opId, patch }) =>
      api.patch<ModuleOperation>(
        `/api/v1/planning/tech-card-modules/${moduleId}/operations/${opId}`,
        patch,
      ),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

export function useDeleteModuleOperation(moduleId: string | null) {
  const invalidate = useInvalidate();
  return useMutation<unknown, Error, { opId: string }>({
    mutationFn: ({ opId }) =>
      api.delete(`/api/v1/planning/tech-card-modules/${moduleId}/operations/${opId}`),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

/** Материалы строки — НАБОРОМ целиком, как у техкарты (PUT одной транзакцией). */
export function useSetModuleMaterials(moduleId: string | null) {
  const invalidate = useInvalidate();
  return useMutation<TechCardMaterial[], Error, { opId: string; materials: MaterialInput[] }>({
    mutationFn: ({ opId, materials }) =>
      api.put<TechCardMaterial[]>(
        `/api/v1/planning/tech-card-modules/${moduleId}/operations/${opId}/materials`,
        materials.map((m, i) => ({ ...m, seq: m.seq ?? i })),
      ),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

/**
 * Вставка системы в карту.
 *
 * Отказ НЕ тостуем: самый частый — 409 «эта система уже вставлена», и место
 * ему в открытом диалоге рядом с кнопкой, вместе с предложением «Заменить», а
 * не всплывашкой поверх закрывшегося окна.
 */
export function useInsertCardModule(cardId: string | null) {
  const invalidate = useInvalidate();
  return useMutation<ModuleApplyResult, Error, { moduleId: string }>({
    mutationFn: ({ moduleId }) =>
      api.post<ModuleApplyResult>(`/api/v1/planning/tech-cards/${cardId}/modules/insert`, {
        module_id: moduleId,
        dry_run: false,
      }),
    onSuccess: invalidate,
  });
}

export function useReplaceCardModule(cardId: string | null) {
  const invalidate = useInvalidate();
  return useMutation<ModuleApplyResult, Error, { oldModuleId: string; newModuleId: string }>({
    mutationFn: ({ oldModuleId, newModuleId }) =>
      api.post<ModuleApplyResult>(`/api/v1/planning/tech-cards/${cardId}/modules/replace`, {
        old_module_id: oldModuleId,
        new_module_id: newModuleId,
        dry_run: false,
      }),
    onSuccess: invalidate,
  });
}

export function useRemoveCardModule(cardId: string | null) {
  const invalidate = useInvalidate();
  return useMutation<unknown, Error, { moduleId: string }>({
    mutationFn: ({ moduleId }) =>
      api.delete(`/api/v1/planning/tech-cards/${cardId}/modules/${moduleId}`),
    onSuccess: invalidate,
    onError: notifyFailure,
  });
}

// ── Производные для экрана ───────────────────────────────────────────────────

/**
 * Отказ «система уже вставлена» — единственный, на который у экрана есть ответ
 * действием: предложить замену вместо повторной вставки.
 *
 * Гейт СТРОГИЙ — статус ровно 409 плюс код `module_already_inserted`, если он
 * приехал. Причина (ревью head-web по #967): предлагать «Заменить её» можно
 * только когда сервер подтвердил, что система в карте действительно стоит. На
 * 500 или обрыве сети мы этого не знаем, и кнопка звала бы заменить неизвестно
 * что — вместо «сохранить не удалось, попробуйте ещё раз».
 *
 * Код читается из `rawDetail` НАПРЯМУЮ: `ApiError` кладёт туда сам объект
 * `detail`, а не конверт `{detail: …}`. Прежняя версия искала
 * `rawDetail.detail.code`, всегда получала `undefined` и держалась на голом
 * статусе — то есть проверка кода была мёртвой.
 */
export function isAlreadyInsertedError(err: unknown): boolean {
  if ((err as { status?: number })?.status !== 409) return false;
  const detail = (err as { rawDetail?: unknown })?.rawDetail;
  if (detail && typeof detail === 'object' && 'code' in detail) {
    return String((detail as { code: unknown }).code) === 'module_already_inserted';
  }
  // 409 без структурированного кода — всё равно конфликт вставки: другого 409
  // у этих ручек нет.
  return true;
}

/** Диапазон расчётной урожайности системы: «45–55 ц/га», «от 45 ц/га», «не задана». */
export function formatYieldRange(module: Pick<SystemModule, 'target_yield_min_c_ha' | 'target_yield_max_c_ha'>): string {
  const min = module.target_yield_min_c_ha ?? null;
  const max = module.target_yield_max_c_ha ?? null;
  if (min !== null && max !== null) return `${min}–${max} ц/га`;
  if (min !== null) return `от ${min} ц/га`;
  if (max !== null) return `до ${max} ц/га`;
  return 'не задана';
}

/**
 * «в 2 картах» / «нигде не используется» — счётчик словами, а не голой цифрой.
 *
 * Склоняет общий `pluralRu`, а не самодельная лесенка `if (count < 5)`: та
 * врала на втором десятке — «в 21 картах» вместо «в 21 карте» (ревью head-web).
 */
export function formatUsage(count: number): string {
  if (!count) return 'нигде не используется';
  return `в ${count} ${pluralRu(count, ['карте', 'картах', 'картах'])}`;
}

/** «1 обработка» / «2 обработки» / «5 обработок» — подпись состава системы. */
export function formatTreatments(count: number): string {
  return `${count} ${pluralRu(count, ['обработка', 'обработки', 'обработок'])}`;
}
